import { Link, useForm } from "@inertiajs/react"

interface Option {
  id: number
  name: string
}
interface Props {
  todo: Record<string, unknown>
  users: Option[]
  notifiableUsers?: Option[]
  errors?: Record<string, string[]>
  isEdit?: boolean
}

export default function TodoForm({
  todo,
  users,
  notifiableUsers,
  errors,
  isEdit,
}: Props) {
  const initialNotifyIds = Array.isArray(todo.notify_user_ids)
    ? (todo.notify_user_ids as number[])
    : []

  const { data, setData, post, patch, processing, transform } = useForm({
    title: todo.title ?? "",
    description: todo.description ?? "",
    due_date: todo.due_date ?? "",
    manager_id: todo.manager_id ?? "",
    notify_user_ids: initialNotifyIds,
  })

  transform((data) => {
    const { notify_user_ids, ...todoFields } = data
    return { marketing_todo: todoFields, notify_user_ids }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isEdit) {
      patch(`/marketing_todos/${String(todo.id)}`)
    } else {
      post("/marketing_todos")
    }
  }

  const managerIdNum =
    typeof data.manager_id === "string" || typeof data.manager_id === "number"
      ? Number(data.manager_id) || null
      : null
  const visibleNotifiable = (notifiableUsers ?? []).filter(
    (u) => u.id !== managerIdNum,
  )
  const selectedManager = managerIdNum
    ? users.find((u) => u.id === managerIdNum)
    : null

  return (
    <form onSubmit={handleSubmit} className="max-w-3xl space-y-4">
      <div>
        <label className="block font-medium">Title</label>
        <input
          type="text"
          value={data.title as string}
          onChange={(e) => setData("title", e.target.value)}
          className="w-full rounded border px-3 py-2"
        />
        {errors?.title && (
          <p className="text-hpi-red text-sm">{errors.title.join(", ")}</p>
        )}
      </div>
      <div>
        <label className="block font-medium">Due Date</label>
        <input
          type="date"
          value={data.due_date as string}
          onChange={(e) => setData("due_date", e.target.value)}
          className="w-full rounded border px-3 py-2"
        />
      </div>
      <div>
        <label className="block font-medium">Manager</label>
        <select
          value={data.manager_id as string}
          onChange={(e) => setData("manager_id", e.target.value)}
          className="w-full rounded border px-3 py-2"
        >
          <option value="">Select...</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block font-medium">Description</label>
        <textarea
          value={data.description as string}
          onChange={(e) => setData("description", e.target.value)}
          className="w-full rounded border px-3 py-2"
          rows={4}
        />
      </div>

      {notifiableUsers && (
        <div className="border-hpi-light-gray rounded border bg-gray-50 p-4">
          <h3 className="mb-3 font-semibold">Notify</h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 md:grid-cols-3">
            {selectedManager && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-green-600">&#10003;</span>
                <span className="font-semibold">
                  {selectedManager.name} (Manager)
                </span>
              </div>
            )}
            {visibleNotifiable.map((user) => (
              <label key={user.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={data.notify_user_ids.includes(user.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setData("notify_user_ids", [
                        ...data.notify_user_ids,
                        user.id,
                      ])
                    } else {
                      setData(
                        "notify_user_ids",
                        data.notify_user_ids.filter((id) => id !== user.id),
                      )
                    }
                  }}
                  className="border-hpi-light-gray rounded"
                />
                <span>{user.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-4">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {isEdit ? "Update" : "Create"}
        </button>
        <Link
          href="/marketing_todos"
          className="hover:bg-hpi-pale-gray rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
